var _ = require('underscore');
var $ = require('jquery');
var TorsoView = require('backbone-torso/modules/View');
var History = require('backbone-torso/modules/history');
var applicationUtils = require('./../../applicationUtils');
var sessionStorageService = require('./../../storage/sessionStorageService');
var TimeoutWidgetView = require('./../../timeout/TimeoutWidgetView');
var eregAuthBarcodePerspectiveTemplate = require('./eregAuthBarcodePerspectiveTemplate.hbs');
var EregAuthBarcodeModel = require('./EregAuthBarcodeModel');
var SecurityQuestionView = require('./EregAuthBarcodeSecurityQuestionView');
var EregAuthBarcodeView = require('./EregAuthBarcodeView');
var AuthenticationFooterView = require('./../../authentication/AuthenticationFooterView');
var EregAuthErrorView = require('./errors/EregAuthErrorView');
var NoBarcodeDataErrorView = require('./errors/NoBarcodeDataErrorView');
var InvalidBarcodeDataErrorView = require('./errors/InvalidBarcodeDataErrorView.js');
var SecurityQuestionResponseInvalidErrorView = require('./errors/SecurityQuestionResponseInvalidErrorView.js');
var userService = require('./../../user/userService');
var barcodeScanningService = require('./barcodeScanningService');
var PreviewBehavior = require('./../../behaviors/PreviewBehavior');
var wizardUtils = require('./../../wizard/wizardUtils');

var PAGE_CONTEXT = {
  START_SCAN: 'startScan',
  SECURITY_QUESTION: 'securityQuestion',
  ERROR: 'error'
};
// auth idle after and timeout countdown in seconds
var AUTH_IDLE_AFTER = 20;
var AUTH_TIMEOUT_COUNTDOWN = 10;

var ALLOWED_NUMBER_OF_BARCODE_RETRIES = 1;

var SERIAL_NUMBER_PROPERTY_NAME = sessionStorageService.SESSION_STORAGE_KEYS.serialNumber;

/**
 * Perspective to display the barcode authentication workflow for tablets
 *
 * @class EregAuthBarcodePerspective
 * @constructor
 */
var EregAuthBarcodePerspective = TorsoView.extend({
  className: 'brec-authentication tablet-authentication',
  moduleNamespace: 'eregAuthBarcodePerspective',
  template: eregAuthBarcodePerspectiveTemplate,

  behaviors: {
    previewBehavior: {
      behavior: PreviewBehavior,
      _setPreviewOptions: function(options) {
        this.view._authView.setPreviewOptions(options);
      }
    }
  },

  injectionSites: {
    'main': '_authView',
    'security-question': '_securityQuestionView',
    'error': '_errorView',
    'footer': '_footerView'
  },

  prepareFields: {
    PAGE_CONTEXT: PAGE_CONTEXT
  },

  /**
   * @method initialize
   * @param options {Object} optional properties
   *   @param options.context {String} The context of the perspective
   * @override
   */
  initialize: function(options) {
    options = options || {};
    _.bindAll(this, '_processAuthResponse', '_navigateToPage', '_processAuthenticationFailure', '_resetSubmitState');
    this._authView = new EregAuthBarcodeView();
    this._securityQuestionView = new SecurityQuestionView();
    this._footerView = new AuthenticationFooterView({isPatient: true});
    this._authModel = new EregAuthBarcodeModel();
    this._authModel.set('serialNumber', sessionStorageService.getItem(SERIAL_NUMBER_PROPERTY_NAME));
    this._resetBarcodeRetriesCount();
    this.set('numSecurityQuestionRetries', 0);
    this.set('context', options.context || PAGE_CONTEXT.START_SCAN);
    this.on('change:context', this.render);
    this.listenTo(barcodeScanningService, 'change:barcodeScanningFinished', this._barcodeScanningFinished);
    this.listenTo(this._securityQuestionView, 'authenticatePatient', this._authenticatePatient);
    this.listenTo(this._securityQuestionView, 'cancelAuthentication', this._cancelAuthentication);
  },

  /**
   * Updates the state of this widget.
   * @method updatePageContext
   * @param context {String} context of the page
   */
  updatePageContext: function(context) {
    this.set('context', context);
  },

  /**
   * Authenticates patient
   * @method _authenticatePatient
   * @private
   */
  _authenticatePatient: function() {
    if (!this.get('submitting')) {
      this.set('submitting', true);
      this._setAuthenticationPayload();
      this._authModel.authenticate()
          .then(this._processAuthResponse)
          .then(userService.resetAndFetchModels)
          .then(this._navigateToPage)
          .fail(this._processAuthenticationFailure);
    }
  },

  /**
   * Updates perspective state on authentication failure
   * @method _processAuthenticationFailure
   * @private
   */
  _processAuthenticationFailure: function() {
    var numSecurityQuestionRetries = this.get('numSecurityQuestionRetries');
    var tryAgain = numSecurityQuestionRetries < 1;
    this._disposeErrorView();
    this._errorView = new SecurityQuestionResponseInvalidErrorView({
      tryAgain: tryAgain
    });
    this.listenTo(this._errorView, 'retrySecurityQuestion', this._retrySecurityQuestion);
    this.updatePageContext(PAGE_CONTEXT.ERROR);
    this.set('numSecurityQuestionRetries', numSecurityQuestionRetries + 1);
    this._resetSubmitState();
  },

  /**
   * Called on authentication success. Clears out models and sets the appointment's externalId on the perspective
   *
   * @method _processAuthResponse
   * @param response The response from the barcode authentication endpoint
   *   @param response.externalId {String} ExternalId of the appointment the patient is attempting to check in to
   * @private
   */
  _processAuthResponse: function(response) {
    var appointmentExternalId = response.externalId;
    this.set('appointmentExternalId', appointmentExternalId);
    userService.set('authenticated', true);
  },

  /**
   * Called on authentication success. Navigates the patient to the checkin/queued page based on the wizard steps.
   * @method _navigateToPage
   * @private
   */
  _navigateToPage: function() {
    var appointmentExternalId = this.get('appointmentExternalId');
    var patientVecnaId = this._authModel.get('patientVecnaId');
    //Navigate takes a hash, not a full url
    var redirectHash = wizardUtils.generateRootRegHash({
      patientVecnaId: patientVecnaId,
      appointmentExternalId: appointmentExternalId
    });
    History.navigate(redirectHash, {trigger: true});
  },

  /**
   * Resets the submission state of the perspective. This is used to prevent sending multiple
   * authentication requests while processing.
   * @method _resetSubmitState
   * @private
   */
  _resetSubmitState: function() {
    this.set('submitting', false);
    this._securityQuestionView.setSubmitState(false);
  },

  /**
   * Creates payload for barcode authentication
   * @method _setAuthenticationPayload
   * @private
   */
  _setAuthenticationPayload: function() {
    var monthDayOfBirth = this._securityQuestionView.formatDob();
    this._authModel.set('dob', monthDayOfBirth);
    var createVisitOnFailedSecurityQuestion = false;
    if (this.get('numSecurityQuestionRetries') && this.get('numSecurityQuestionRetries') > 0) {
      createVisitOnFailedSecurityQuestion = true;
    }
    this._authModel.set('createVisitOnFailedSecurityQuestion', createVisitOnFailedSecurityQuestion);
  },

  /**
   * Navigates to the start screen
   * @method _cancelAuthentication
   * @private
   */
  _cancelAuthentication: function() {
    History.navigate('tablet/serialNumber/' + sessionStorageService.getItem(SERIAL_NUMBER_PROPERTY_NAME), {trigger: true});
  },

  /**
   * Updates perspective state based on barcode scanning results
   * @method _barcodeScanningFinished
   * @private
   */
  _barcodeScanningFinished: function() {
    /**
     * _barcodeScanningFinished is called when there is a change to barcodeScanningService's barcodeScanningFinished including when it is set to false
     * This area handles any clean up from prior scans and exits out if it is called when scanning starts or is retried
     */
    if (!barcodeScanningService.isBarcodeScanningFinished()) {
      if (this._timeoutWidget) {
        this._timeoutWidget.stopAndClose();
      }
      this._clearAuthModel();
      return;
    }

    if (barcodeScanningService.isCanceled()) {
      this._resetBarcodeRetriesCount();
      this._cancelAuthentication();
      return;
    }

    this._startTimeout();

    var patientVecnaId = barcodeScanningService.getPatientVecnaId();
    var appointmentVecnaId = barcodeScanningService.getAppointmentVecnaId();
    if (patientVecnaId && appointmentVecnaId) {
      this._resetBarcodeRetriesCount();
      this._transitionToSecurityQuestionView(patientVecnaId, appointmentVecnaId);
    } else {
      this._transitionToBarcodeErrorView();
    }
  },

  /**
   * Transitions to the security question view and sets the patient and appointment Vecna IDs on the auth model
   * @method _transitionToSecurityQuestionView
   * @param patientVecnaId
   * @param appointmentVecnaId
   * @private
   */
  _transitionToSecurityQuestionView: function(patientVecnaId, appointmentVecnaId) {
    this._authModel.set('patientVecnaId', patientVecnaId);
    this._authModel.set('appointmentVecnaId', appointmentVecnaId);
    this.updatePageContext(PAGE_CONTEXT.SECURITY_QUESTION);
  },

  /**
   * Transitions to the error view based on the type of error
   * @method _transitionToBarcodeErrorView
   * @private
   */
  _transitionToBarcodeErrorView: function() {
    var error = barcodeScanningService.getError();
    var tryAgain = false;
    this._disposeErrorView();
    if (barcodeScanningService.BARCODE_ERRORS.INVALID_BARCODE === error) {
      this._errorView = new InvalidBarcodeDataErrorView();
    } else if (barcodeScanningService.BARCODE_ERRORS.NO_DATA === error
        || barcodeScanningService.BARCODE_ERRORS.SCANNING_ERROR === error) {
      tryAgain = this._hasBarcodeRetriesRemaining();
      this._errorView = new NoBarcodeDataErrorView({
        tryAgain: tryAgain
      });
      this.listenTo(this._errorView, 'retryBarcode', this._retryBarcodeScanning);
    } else if (barcodeScanningService.BARCODE_ERRORS.TIMEOUT === error) {
      this._errorView = new EregAuthErrorView();
    }
    if (!tryAgain) {
      this._authModel.createFailedVisit();
      this._resetBarcodeRetriesCount();
    }
    this.updatePageContext(PAGE_CONTEXT.ERROR);
  },

  /**
   * Updates the page context to start scan and retries a barcode scan
   * @method _retryBarcodeScanning
   * @private
   */
  _retryBarcodeScanning: function() {
    this._iterateBarcodeRetries();
    this.updatePageContext(PAGE_CONTEXT.START_SCAN);
    barcodeScanningService.startBarcodeScanAndPolling();
  },

  /**
   * Updates the page context to retry security question
   * @method _retrySecurityQuestion
   * @private
   */
  _retrySecurityQuestion: function() {
    this.updatePageContext(PAGE_CONTEXT.SECURITY_QUESTION);
  },

  /**
   * Iterates the count of barcode attempts that have occurred
   * @method _iterateBarcodeRetries
   * @private
   */
  _iterateBarcodeRetries: function() {
    var counter = this.get('numBarcodeRetries');
    this.set('numBarcodeRetries', counter++);
  },

  /**
   * Returns true if have not exhausted number of barcode retries
   * @method _hasBarcodeRetriesRemaining
   * @private
   */
  _hasBarcodeRetriesRemaining: function() {
    var numBarcodeRetries = this.get('numBarcodeRetries');
    return numBarcodeRetries < ALLOWED_NUMBER_OF_BARCODE_RETRIES;
  },

  /**
   * Resets the count of how many barcode attempts have occurred
   * @method _resetBarcodeAttempts
   * @private
   */
  _resetBarcodeRetriesCount: function() {
    this.set('numBarcodeRetries', 0);
  },

  /**
   * Disposes of the errorView if it exists
   * @method _disposeErrorView
   * @private
   */
  _disposeErrorView: function() {
    if (this._errorView) {
      this.stopListening(this._errorView);
      this.unregisterTrackedView(this._errorView);
      this._errorView._dispose();
    }
  },

  /**
   * Clears patient/information data from the auth model
   * @method _clearAuthModel
   * @private
   */
  _clearAuthModel: function() {
    if (this._authModel) {
      this._authModel.unset('patientVecnaId');
      this._authModel.unset('appointmentVecnaId');
      this._authModel.unset('dob');
    }
  },

  /**
   * Starts a timeout
   * @method _startTimeout
   * @private
   */
  _startTimeout: function() {
    if (!this._timeoutWidget) {
      var timeoutWidgetOptions = {
        logoutUrl: applicationUtils.APPLICATIONS.EREG.getLoginUrl(sessionStorageService.getItem(SERIAL_NUMBER_PROPERTY_NAME)),
        idleAfter: AUTH_IDLE_AFTER,
        warningLength: AUTH_TIMEOUT_COUNTDOWN
      };

      this._timeoutWidget = new TimeoutWidgetView(timeoutWidgetOptions);
      this._timeoutWidget.attachTo($('.timeout-widget'));
    } else {
      this._timeoutWidget.reset();
    }
  },

  /**
   * @method _dispose
   * @override
   * @private
   */
  _dispose: function() {
    if (this._timeoutWidget) {
      this._timeoutWidget.stopAndClose();
      this._timeoutWidget.dispose();
    }
  }

});

EregAuthBarcodePerspective.PAGE_CONTEXT = PAGE_CONTEXT;
module.exports = EregAuthBarcodePerspective;
